import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Star } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ApiService from "../../services/api-service";

const FEEDBACK_TYPES = [
  { value: "general", label: "General Feedback" },
  { value: "content", label: "Content" },
  { value: "aiprompts", label: "AI Prompts" },
  { value: "ux", label: "User Experience" },
  { value: "feature", label: "Feature Request" },
  { value: "bug", label: "Bug / Problem" },
  { value: "other", label: "Other" },
];

const RECOMMEND_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "No" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MESSAGE_MIN = 5;
const MESSAGE_MAX = 2000;

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The logged-in user's email, pre-filled into the optional email field.
  defaultEmail?: string;
}

type Errors = Partial<Record<"rating" | "message" | "email", string>>;

const chipClass = (active: boolean) =>
  `rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-200 ${
    active
      ? "bg-slate-900 text-white shadow-sm shadow-slate-900/20 hover:bg-slate-900 hover:text-white dark:bg-white dark:text-slate-900 dark:hover:bg-white dark:hover:text-slate-900"
      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300"
  }`;

export function FeedbackDialog({ open, onOpenChange, defaultEmail = "" }: FeedbackDialogProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState("general");
  const [message, setMessage] = useState("");
  const [recommend, setRecommend] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Blocks a second submit fired before React re-renders the disabled button.
  const inFlight = useRef(false);

  const reset = () => {
    setRating(0);
    setHoverRating(0);
    setFeedbackType("general");
    setMessage("");
    setRecommend("");
    setName("");
    setEmail(defaultEmail);
    setErrors({});
    setSubmitError("");
    setSubmitted(false);
  };

  useEffect(() => {
    if (open && !email && defaultEmail) setEmail(defaultEmail);
  }, [open, defaultEmail]);

  const handleOpenChange = (next: boolean) => {
    if (!next && submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!rating) next.rating = "Please choose a rating.";
    const trimmed = message.trim();
    if (trimmed.length < MESSAGE_MIN || trimmed.length > MESSAGE_MAX) {
      next.message = `Please share your feedback (${MESSAGE_MIN} to ${MESSAGE_MAX} characters).`;
    }
    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      next.email = "Please enter a valid email address.";
    }
    return next;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current || submitting) return;

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    inFlight.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      await ApiService.submitFeedback({
        rating,
        feedbackType,
        message: message.trim(),
        recommend: recommend || undefined,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      });
      setSubmitted(true);
    } catch (error) {
      const serverMessage = error instanceof Error ? error.message : "";
      setSubmitError(
        serverMessage && !serverMessage.startsWith("HTTP error")
          ? serverMessage
          : "We couldn't send your feedback. Please check your connection and try again."
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const shownRating = hoverRating || rating;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[min(100vw-1.5rem,34rem)] max-h-[90vh] overflow-y-auto rounded-2xl p-5 sm:p-6">
        {submitted ? (
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" aria-hidden="true" />
            <DialogTitle className="text-lg">Thank you for your feedback!</DialogTitle>
            <DialogDescription>Your feedback will help us make TheDeepam better.</DialogDescription>
            <Button className="mt-2" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Help Us Improve TheDeepam</DialogTitle>
              <DialogDescription>
                Your feedback helps us make TheDeepam better. Tell us what you liked, what we can improve, or what you
                would like to see next.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} noValidate className="space-y-5 mt-2">
              <div className="space-y-2">
                <Label>Overall Experience *</Label>
                <div className="flex items-center gap-1" role="radiogroup" aria-label="Overall experience rating">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={rating === value}
                      aria-label={`${value} ${value === 1 ? "star" : "stars"}`}
                      onMouseEnter={() => setHoverRating(value)}
                      onMouseLeave={() => setHoverRating(0)}
                      onFocus={() => setHoverRating(value)}
                      onBlur={() => setHoverRating(0)}
                      onClick={() => {
                        setRating(value);
                        setErrors((prev) => ({ ...prev, rating: undefined }));
                      }}
                      className="p-1 rounded-md transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          value <= shownRating
                            ? "text-yellow-500 fill-yellow-400"
                            : "text-slate-300 dark:text-slate-600"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {errors.rating && <p className="text-xs text-red-600 dark:text-red-400">{errors.rating}</p>}
              </div>

              <div className="space-y-2">
                <Label>Feedback Type</Label>
                <div className="flex flex-wrap gap-2">
                  {FEEDBACK_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      aria-pressed={feedbackType === type.value}
                      onClick={() => setFeedbackType(type.value)}
                      className={chipClass(feedbackType === type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-message">Your Feedback *</Label>
                <Textarea
                  id="feedback-message"
                  value={message}
                  maxLength={MESSAGE_MAX}
                  onChange={(event) => {
                    setMessage(event.target.value);
                    setErrors((prev) => ({ ...prev, message: undefined }));
                  }}
                  placeholder="Tell us about your experience, suggestion, or idea..."
                  rows={5}
                  aria-invalid={!!errors.message}
                />
                {errors.message && <p className="text-xs text-red-600 dark:text-red-400">{errors.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="feedback-name">Name (optional)</Label>
                  <Input id="feedback-name" value={name} maxLength={100} onChange={(event) => setName(event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feedback-email">Email (optional)</Label>
                  <Input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p className="text-xs text-red-600 dark:text-red-400">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Would you recommend TheDeepam to others?</Label>
                <div className="flex flex-wrap gap-2">
                  {RECOMMEND_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={recommend === option.value}
                      onClick={() => setRecommend(recommend === option.value ? "" : option.value)}
                      className={chipClass(recommend === option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                >
                  {submitError}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-800 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-300"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Sending...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
